import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, FileText, Building2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DataRoomLanderPage — sidebar nav target.
 *
 * Fetches the user's companies; if exactly one company exists, redirects
 * straight into its detail page anchored at the Data Room section.
 * If multiple companies exist, shows a small picker. If none, prompts the
 * user to add a company first.
 */
const DataRoomLanderPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active) return;
        // /api/companies returns either {companies: [...]} or a bare array
        // depending on the response model — accept both shapes.
        const list = Array.isArray(r.data?.companies)
          ? r.data.companies
          : (Array.isArray(r.data) ? r.data : []);
        setCompanies(list);
        if (list.length === 1) {
          navigate(`/dashboard/companies/${list[0].id}#data-room`, { replace: true });
        }
      } catch (e) {
        if (active) setErr(e.response?.data?.detail || 'Failed to load companies');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token, navigate]);

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="data-room-lander">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#059669]/15 border border-[#059669]/30 flex items-center justify-center">
            <FileText className="w-5 h-5 text-[#059669]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-white">Data Room</h1>
            <p className="text-sm text-gray-400">Secure document review for your investors and advisors.</p>
          </div>
        </div>

        {(loading || (!err && companies.length === 1)) && (
          <div className="bg-[#111827] border border-gray-800 rounded-lg p-6 flex items-center gap-3 text-gray-300" data-testid="data-room-lander-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Opening your Data Room…
          </div>
        )}

        {err && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300 flex items-start gap-2" data-testid="data-room-lander-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
          </div>
        )}

        {!loading && !err && companies.length === 0 && (
          <div className="bg-[#111827] border border-gray-800 rounded-lg p-8 text-center" data-testid="data-room-lander-empty">
            <Building2 className="w-10 h-10 text-gray-600 mx-auto mb-3" strokeWidth={1.4} />
            <p className="text-sm text-gray-300">You don&apos;t have any companies yet.</p>
            <p className="text-xs text-gray-500 mt-1">Add a company first to use the Data Room.</p>
            <button
              type="button"
              onClick={() => navigate('/dashboard/companies/new')}
              className="mt-5 inline-flex items-center gap-1.5 bg-[#059669] hover:bg-[#00d96e] text-black font-semibold rounded px-4 py-2 text-sm"
              data-testid="data-room-lander-add-company"
            >
              Add a Company
            </button>
          </div>
        )}

        {!loading && companies.length > 1 && (
          <div className="space-y-2" data-testid="data-room-lander-picker">
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
              Choose a company to open its Data Room
            </p>
            {companies.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => navigate(`/dashboard/companies/${c.id}#data-room`)}
                className="w-full text-left bg-[#111827] hover:bg-[#1c232e] border border-gray-800 hover:border-gray-700 rounded-lg p-4 flex items-center justify-between gap-3 transition-colors"
                data-testid={`data-room-lander-company-${c.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-md bg-slate-800 flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-gray-300" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{c.company_name}</p>
                    <p className="text-[11px] text-gray-500">{c.entity_type} · {c.state}</p>
                  </div>
                </div>
                <span className="text-xs text-[#059669]">Open Data Room →</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default DataRoomLanderPage;
